import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { Icon } from "@iconify/react";
import { DatePicker, TimePicker, ConfigProvider } from "antd";
import thTH from "antd/locale/th_TH";
import dayjs from "dayjs";
import SelectStepModal from "../Modal/Contract/SelectStepModal";
import api from "../../../utils/axios";
import Swal from "sweetalert2";
import { getData, getDataWithComp } from "../../../utils/SamuiUtils";

const uuid = () =>
  crypto.randomUUID ? crypto.randomUUID() : "id-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);

const SearchableItemSelect = ({ value, options, onChange, groupMap = {} }) => {
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);

  const selected = options.find((o) => o.Item_Id === value) || null;
  const getLabel = (item) => {
    const g = groupMap[String(item.Item_Group)] || "";
    return g ? `${g} - ${item.Item_Name}` : item.Item_Name;
  };

  useEffect(() => {
    const handler = (e) => {
      if (!containerRef.current?.contains(e.target) && !dropdownRef.current?.contains(e.target)) {
        setIsOpen(false); setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const openDropdown = () => {
    if (containerRef.current) {
      const r = containerRef.current.getBoundingClientRect();
      setDropdownPos({ top: r.bottom + 2, left: r.left, width: r.width });
    }
    setIsOpen(true); setSearch("");
  };

  const filtered = search
    ? options.filter((o) => {
        const s = search.toLowerCase();
        return (o.Item_Code || "").toLowerCase().includes(s) ||
          (o.Item_Name || "").toLowerCase().includes(s) ||
          (groupMap[String(o.Item_Group)] || "").toLowerCase().includes(s);
      })
    : options;

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <div style={{ display: "flex", border: "1px solid #d1d5db", borderRadius: 6, overflow: "hidden", height: 34, backgroundColor: "#fff" }}>
        <input
          type="text"
          value={isOpen ? search : (selected ? getLabel(selected) : "")}
          onChange={(e) => { setSearch(e.target.value); if (!isOpen) openDropdown(); }}
          onFocus={openDropdown}
          placeholder={isOpen ? "พิมพ์ชื่อ/รหัสสินค้า..." : "-- เลือกสินค้า --"}
          style={{ flex: 1, border: "none", padding: "0 8px", outline: "none", fontSize: 13, backgroundColor: "transparent", height: "100%", lineHeight: "34px" }}
        />
        {value && (
          <button type="button" onClick={() => { onChange(null); setSearch(""); setIsOpen(false); }}
            style={{ border: "none", background: "none", padding: "0 5px", color: "#9ca3af", cursor: "pointer" }}>×</button>
        )}
        <button type="button" onClick={() => isOpen ? (setIsOpen(false), setSearch("")) : openDropdown()}
          style={{ border: "none", borderLeft: "1px solid #d1d5db", background: "#f9fafb", padding: "0 8px", cursor: "pointer", color: "#6b7280", fontSize: 12 }}>▾</button>
      </div>
      {isOpen && ReactDOM.createPortal(
        <div ref={dropdownRef} style={{ position: "fixed", top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width, zIndex: 9999, backgroundColor: "#fff", border: "1px solid #d1d5db", borderRadius: 6, maxHeight: 200, overflowY: "auto", boxShadow: "0 4px 12px rgba(0,0,0,0.12)" }}>
          <div onClick={() => { onChange(null); setSearch(""); setIsOpen(false); }}
            style={{ padding: "7px 10px", cursor: "pointer", color: "#9ca3af", fontSize: 13 }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#f3f4f6")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
            -- เลือกสินค้า --
          </div>
          {filtered.map((item) => (
            <div key={item.Item_Id} onClick={() => { onChange(item); setSearch(""); setIsOpen(false); }}
              style={{ padding: "7px 10px", cursor: "pointer", fontSize: 13, backgroundColor: value === item.Item_Id ? "#fef3c7" : "transparent" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#f3f4f6")}
              onMouseLeave={(e) => (e.currentTarget.style.background = value === item.Item_Id ? "#fef3c7" : "transparent")}>
              {getLabel(item)}
            </div>
          ))}
          {filtered.length === 0 && <div style={{ padding: "7px 10px", color: "#9ca3af", fontSize: 13 }}>ไม่พบสินค้า</div>}
        </div>,
        document.body
      )}
    </div>
  );
};

export default function AddJobWorkModal({
  isOpen,
  onClose,
  onSave,
  serviceList = [],
  contractServices = [],
  currentWorksCount = 0,
  latestRoundDate = null,
  existingDates = [],
  contractEndDate = null,
}) {
  const [selectedService, setSelectedService] = useState("");
  const [selectedServiceId, setSelectedServiceId] = useState(null);
  const [steps, setSteps] = useState([]);
  const [serviceSteps, setServiceSteps] = useState([]);
  const [loadingSteps, setLoadingSteps] = useState(false);
  const [unitList, setUnitList] = useState([]);
  const [showStepModal, setShowStepModal] = useState(false);

  // Assign fields
  const [dateMode, setDateMode] = useState("date"); // "date" | "month"
  const [assignDate, setAssignDate] = useState(null); // dayjs object
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [teamId, setTeamId] = useState("");
  const [timeStart, setTimeStart] = useState(dayjs("08:00", "HH:mm"));
  const [timeEnd, setTimeEnd] = useState(dayjs("09:00", "HH:mm"));
  const [teamList, setTeamList] = useState([]);

  // Item list for อุปกรณ์/สารเคมี
  const [workItemList, setWorkItemList] = useState([]);
  const [groupMap, setGroupMap] = useState({});
  const [tempEquipGroupIds, setTempEquipGroupIds] = useState(new Set());
  const [focusedQtyIdx, setFocusedQtyIdx] = useState(null);

  useEffect(() => {
    const fetchUnits = async () => {
      try {
        const compId = window.localStorage.getItem("company");
        const units = await getData(
          "Tb_Set_Unit",
          `Unit_Status = 'Y'${compId ? ` AND Comp_Id = ${compId}` : ""} ORDER BY Unit_Name`
        );
        const list = Array.isArray(units) ? units : Array.isArray(units?.data) ? units.data : [];
        setUnitList([""].concat(list.map((u) => u.Unit_Name).filter(Boolean)));
      } catch {
        setUnitList(["", "มล.", "ลิตร", "กก.", "กรัม"]);
      }
    };

    const fetchTeams = async () => {
      try {
        const compId = window.localStorage.getItem("company");
        const res = await api.get(`/get-teams?Comp_Id=${compId}`);
        if (res.data?.status === "OK") {
          setTeamList(
            (res.data.result || [])
              .filter((t) => t.Team_Status === "A")
              .map((t) => ({ id: t.Team_Id, name: t.Team_Name }))
          );
        }
      } catch {}
    };

    const fetchItems = async () => {
      try {
        const [items, groups] = await Promise.all([
          getDataWithComp("Tb_Set_Item", "AND Item_Status = 'Y' ORDER BY Item_Short_Name, Item_Name", { fields: ["Item_Id", "Item_Code", "Item_Name", "Item_Unit_ST", "Item_Group"] }),
          getDataWithComp("Tb_Set_ItmGroup", "AND IsShowInWork = 1 AND Group_Status = 'Y'", { fields: ["Group_Id", "Group_Name"] }),
        ]);
        const allItems = Array.isArray(items) ? items : [];
        const allGroups = Array.isArray(groups) ? groups : [];
        const map = {};
        allGroups.forEach((g) => { map[String(g.Group_Id)] = g.Group_Name; });
        setGroupMap(map);
        if (allGroups.length > 0) {
          const allowed = new Set(allGroups.map((g) => String(g.Group_Id)));
          setWorkItemList(allItems.filter((i) => allowed.has(String(i.Item_Group))));
        } else {
          setWorkItemList(allItems);
        }
      } catch {}
      try {
        const teRes = await api.get("/app/temp-equip-groups");
        const teGroups = teRes.data?.result || [];
        setTempEquipGroupIds(new Set(teGroups.map((g) => String(g.Group_Id))));
      } catch {}
    };

    fetchUnits();
    fetchTeams();
    fetchItems();
  }, []);

  useEffect(() => {
    if (!selectedService) {
      setSteps([]);
      setServiceSteps([]);
      setSelectedServiceId(null);
      return;
    }
    const srv = serviceList.find((x) => x.Service_Name === selectedService);
    if (!srv?.Service_Id) return;
    setSelectedServiceId(srv.Service_Id);
    fetchServiceSteps(srv.Service_Id);
  }, [selectedService, serviceList]);

  const fetchServiceSteps = async (serviceId) => {
    try {
      setLoadingSteps(true);
      const res = await api.get(`/get-service-steps/${serviceId}`);
      if (res.data.status === "OK") {
        const stepsData = res.data.result || [];
        setServiceSteps(stepsData);
        const defaultSteps = stepsData
          .filter((st) => st.IsDefault === 1 || st.IsDefault === true)
          .map((st) => ({
            id: st.Step_Id,
            title: st.Step_Title || "",
            qty: st.Step_Qty != null ? String(st.Step_Qty) : "",
            unit: st.Step_Unit != null ? String(st.Step_Unit) : "",
            remark: "",
            checked: false,
            isCollect: false,
            itemGroup: st.Item_Group ?? null,
          }));
        setSteps(defaultSteps);
      } else {
        setServiceSteps([]);
      }
    } catch {
      setServiceSteps([]);
    } finally {
      setLoadingSteps(false);
    }
  };

  const sanitizeNumericInput = (raw) => {
    const noComma = String(raw ?? "").replace(/,/g, "");
    const cleaned = noComma.replace(/[^\d.]/g, "");
    const parts = cleaned.split(".");
    const intPart = parts[0] || "";
    if (parts.length === 1) {
      return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }
    const decPart = parts[1].slice(0, 2);
    const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return `${formattedInt}.${decPart}`;
  };

  const formatQtyDisplay = (raw) => {
    if (raw === null || raw === undefined || raw === "") return "0.00";
    const num = Number(String(raw).replace(/,/g, ""));
    if (Number.isNaN(num)) return "0.00";
    return num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const isTempEquipStep = (st) => {
    const gid = st?.itemGroup != null && st?.itemGroup !== "" ? String(st.itemGroup) : null;
    if (!gid) return false;
    return tempEquipGroupIds.has(gid);
  };

  const updateStep = (index, field, value) => {
    setSteps((prev) => prev.map((st, i) => (i === index ? { ...st, [field]: value } : st)));
  };

  const stepFields = ["title", "qty", "unit", "remark"];

  const handleStepKeyNav = (e, idx, field) => {
    const fi = stepFields.indexOf(field);
    const sel = e.target.selectionStart;
    const len = e.target.value.length;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      document.querySelector(`[data-stepidx="${idx + 1}"][data-field="${field}"]`)?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      document.querySelector(`[data-stepidx="${idx - 1}"][data-field="${field}"]`)?.focus();
    } else if (e.key === "ArrowRight" && sel === len && fi < stepFields.length - 1) {
      e.preventDefault();
      document.querySelector(`[data-stepidx="${idx}"][data-field="${stepFields[fi + 1]}"]`)?.focus();
    } else if (e.key === "ArrowLeft" && sel === 0 && fi > 0) {
      e.preventDefault();
      const prev = document.querySelector(`[data-stepidx="${idx}"][data-field="${stepFields[fi - 1]}"]`);
      if (prev) { prev.focus(); prev.setSelectionRange(prev.value.length, prev.value.length); }
    }
  };

  const updateStepItem = (index, item) => {
    setSteps((prev) => prev.map((st, i) =>
      i !== index ? st : item
        ? { ...st, itemId: item.Item_Id, itemName: item.Item_Name || "", itemCode: item.Item_Code || "", itemGroup: item.Item_Group ?? null, unit: item.Item_Unit_ST || st.unit, isCollect: st.isCollect ?? false }
        : { ...st, itemId: null, itemName: "", itemCode: "", itemGroup: null, isCollect: false }
    ));
  };

  const removeStep = (index) => {
    setSteps((prev) => prev.filter((_, i) => i !== index));
  };

  const today = dayjs().startOf("day");

  const saveJob = () => {
    if (!selectedService) {
      Swal.fire({ icon: "warning", title: "แจ้งเตือน", text: "กรุณาเลือกบริการ", confirmButtonText: "ตกลง" });
      return;
    }
    if (!steps || steps.length === 0) {
      Swal.fire({ icon: "warning", title: "แจ้งเตือน", text: "กรุณาเลือกขั้นตอนการบริการ", confirmButtonText: "ตกลง" });
      return;
    }

    let dateStr;
    if (dateMode === "month") {
      if (!month || !year) {
        Swal.fire({ icon: "warning", title: "แจ้งเตือน", text: "กรุณาเลือกเดือนและปี", confirmButtonText: "ตกลง" });
        return;
      }
      const ceYear = parseInt(year) - 543;
      dateStr = `${ceYear}-${month}-01`;
    } else {
      if (!assignDate) {
        Swal.fire({ icon: "warning", title: "แจ้งเตือน", text: "กรุณาเลือกวันที่บริการ", confirmButtonText: "ตกลง" });
        return;
      }
      if (!teamId) {
        Swal.fire({ icon: "warning", title: "แจ้งเตือน", text: "กรุณาเลือกทีม", confirmButtonText: "ตกลง" });
        return;
      }
      if (!timeStart || !timeEnd) {
        Swal.fire({ icon: "warning", title: "แจ้งเตือน", text: "กรุณาระบุเวลา", confirmButtonText: "ตกลง" });
        return;
      }
      dateStr = assignDate.format("YYYY-MM-DD");
      const todayStr = dayjs().format("YYYY-MM-DD");
      if (dateStr < todayStr) {
        Swal.fire({ icon: "warning", title: "แจ้งเตือน", text: "ไม่สามารถเลือกวันที่ผ่านมาแล้วได้", confirmButtonText: "ตกลง" });
        return;
      }
      if (existingDates.includes(dateStr)) {
        Swal.fire({ icon: "warning", title: "แจ้งเตือน", text: "มีรอบบริการในวันที่นี้แล้ว กรุณาเลือกวันอื่น", confirmButtonText: "ตกลง" });
        return;
      }
      if (contractEndDate && dateStr > contractEndDate) {
        Swal.fire({ icon: "warning", title: "แจ้งเตือน", text: `วันที่เลือกเกินวันหมดสัญญา (${dayjs(contractEndDate).format("DD/MM/") + (dayjs(contractEndDate).year() + 543)})`, confirmButtonText: "ตกลง" });
        return;
      }
    }

    try {
      onSave({
        assignDate: dateStr,
        teamId: dateMode === "date" ? teamId : "",
        timeStart: dateMode === "date" ? timeStart.format("HH:mm") : "08:00",
        timeEnd: dateMode === "date" ? timeEnd.format("HH:mm") : "09:00",
        service: selectedService,
        steps,
      });
      resetState();
      onClose();
    } catch (error) {
      Swal.fire({ icon: "error", title: "เกิดข้อผิดพลาด", text: error.message || "ไม่ทราบสาเหตุ", confirmButtonText: "ตกลง" });
    }
  };

  const resetState = () => {
    setSelectedService("");
    setSelectedServiceId(null);
    setSteps([]);
    setServiceSteps([]);
    setDateMode("date");
    setAssignDate(null);
    setMonth("");
    setYear("");
    setTeamId("");
    setTimeStart(dayjs("08:00", "HH:mm"));
    setTimeEnd(dayjs("09:00", "HH:mm"));
  };

  useEffect(() => {
    if (!isOpen) resetState();
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      <style>{`
        .ant-picker-dropdown,
        .ant-picker-dropdown-range,
        div[class*="ant-picker-dropdown"],
        div[class*="ant-picker-panel"] { z-index: 9999 !important; }
      `}</style>
      <div className="modal fade show" style={{ display: "block" }} tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "1400px", width: "98%" }}>
          <div className="modal-content border-0 shadow-lg">

            <div className="modal-header" style={{ backgroundColor: "#E13F57", color: "white" }}>
              <h5 className="modal-title" style={{ fontWeight: "bold" }}>
                เพิ่มจ๊อบงานเสริม ครั้งที่ {currentWorksCount + 1}
              </h5>
              <button type="button" className="close" onClick={onClose}>
                <span>&times;</span>
              </button>
            </div>

            <div className="modal-body">

              {/* Assign section */}
              <div className="mb-4">
                {/* Radio mode */}
                <div className="d-flex gap-4 mb-3">
                  {[{ value: "month", label: "กำหนดเดือน" }, { value: "date", label: "กำหนดวันที่" }].map((opt) => (
                    <label key={opt.value} className="d-flex align-items-center gap-2 mb-0" style={{ cursor: "pointer", fontSize: 14, fontWeight: 500, color: "#374151" }}>
                      <input
                        type="radio"
                        name="addJobDateMode"
                        value={opt.value}
                        checked={dateMode === opt.value}
                        onChange={() => setDateMode(opt.value)}
                        style={{ width: 16, height: 16, accentColor: "#E13F57", flexShrink: 0 }}
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>

                {dateMode === "month" ? (
                  <div className="row g-3">
                    <div className="col-auto">
                      <label className="text-sm font-medium text-gray-700 d-block mb-1">เดือน</label>
                      <select
                        style={{ height: 38, minWidth: 160, padding: "0 10px", border: "1px solid #d9d9d9", borderRadius: 6, fontSize: 14, outline: "none", background: "#fff" }}
                        value={month}
                        onChange={(e) => setMonth(e.target.value)}
                      >
                        <option value="">-- เดือน --</option>
                        {[["01","มกราคม"],["02","กุมภาพันธ์"],["03","มีนาคม"],["04","เมษายน"],
                          ["05","พฤษภาคม"],["06","มิถุนายน"],["07","กรกฎาคม"],["08","สิงหาคม"],
                          ["09","กันยายน"],["10","ตุลาคม"],["11","พฤศจิกายน"],["12","ธันวาคม"]
                        ].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </div>
                    <div className="col-auto">
                      <label className="text-sm font-medium text-gray-700 d-block mb-1">ปี (พ.ศ.)</label>
                      <select
                        style={{ height: 38, minWidth: 120, padding: "0 10px", border: "1px solid #d9d9d9", borderRadius: 6, fontSize: 14, outline: "none", background: "#fff" }}
                        value={year}
                        onChange={(e) => setYear(e.target.value)}
                      >
                        <option value="">-- ปี --</option>
                        {Array.from({ length: 5 }, (_, i) => {
                          const be = new Date().getFullYear() + 543 - 1 + i;
                          return <option key={be} value={String(be)}>{be}</option>;
                        })}
                      </select>
                    </div>
                  </div>
                ) : (
                  <div className="row g-3 align-items-end">
                    <div className="col-auto" style={{ minWidth: 180 }}>
                      <label className="text-sm font-medium text-gray-700 d-block mb-1">วันที่บริการ</label>
                      <ConfigProvider locale={thTH}>
                        <DatePicker
                          style={{ width: "100%", height: 38 }}
                          value={assignDate}
                          onChange={(d) => setAssignDate(d)}
                          disabledDate={(d) => {
                            if (!d) return false;
                            if (d.isBefore(today)) return true;
                            if (contractEndDate && d.isAfter(dayjs(contractEndDate), "day")) return true;
                            if (existingDates.includes(d.format("YYYY-MM-DD"))) return true;
                            return false;
                          }}
                          format={(d) => d.format("DD/MM/") + (d.year() + 543)}
                          placeholder="วว/ดด/ปปปป"
                          allowClear={false}
                          getPopupContainer={() => document.body}
                          popupStyle={{ zIndex: 1060 }}
                        />
                      </ConfigProvider>
                    </div>
                    <div className="col-auto" style={{ minWidth: 160 }}>
                      <label className="text-sm font-medium text-gray-700 d-block mb-1">ทีม <span style={{ color: "#ef4444" }}>*</span></label>
                      <select
                        style={{ height: 38, width: "100%", padding: "0 10px", border: "1px solid #d9d9d9", borderRadius: 6, fontSize: 14, boxSizing: "border-box", outline: "none", background: "#fff" }}
                        value={teamId}
                        onChange={(e) => setTeamId(e.target.value)}
                      >
                        <option value="">-- เลือกทีม --</option>
                        {teamList.map((t) => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-auto">
                      <label className="text-sm font-medium text-gray-700 d-block mb-1">เวลา</label>
                      <TimePicker
                        style={{ width: 110, height: 38 }}
                        format="HH:mm"
                        value={timeStart}
                        onChange={(t) => setTimeStart(t)}
                        allowClear={false}
                        placeholder="00:00"
                        getPopupContainer={() => document.body}
                        popupStyle={{ zIndex: 1060 }}
                      />
                    </div>
                    <div className="col-auto">
                      <label className="text-sm font-medium text-gray-700 d-block mb-1">ถึงเวลา</label>
                      <TimePicker
                        style={{ width: 110, height: 38 }}
                        format="HH:mm"
                        value={timeEnd}
                        onChange={(t) => setTimeEnd(t)}
                        allowClear={false}
                        placeholder="00:00"
                        getPopupContainer={() => document.body}
                        popupStyle={{ zIndex: 1060 }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* TABLE */}
              <div className="table-responsive" style={{ maxHeight: "360px", overflowY: "auto" }}>
                <table className="table table-bordered mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: "5%", textAlign: "center", backgroundColor: "#E13F57", color: "#fff" }}>ลำดับ</th>
                      <th style={{ width: "27%", fontSize: "14px", backgroundColor: "#E13F57", color: "#fff" }}>บริการ / ขั้นตอน</th>
                      <th style={{ width: "20%", backgroundColor: "#E13F57", color: "#fff" }}>อุปกรณ์ / สารเคมี</th>
                      <th style={{ width: "10%", textAlign: "center", backgroundColor: "#E13F57", color: "#fff" }}>ปริมาณ</th>
                      <th style={{ width: "8%", textAlign: "center", backgroundColor: "#E13F57", color: "#fff" }}>หน่วย</th>
                      <th style={{ width: "8%", textAlign: "center", backgroundColor: "#E13F57", color: "#fff" }}>วางไว้ / เก็บคืน</th>
                      <th style={{ width: "18%", backgroundColor: "#E13F57", color: "#fff" }}>หมายเหตุ</th>
                      <th style={{ width: "5%", backgroundColor: "#E13F57", color: "#fff" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="text-center align-middle">1</td>
                      <td className="align-middle">
                        <select
                          className="form-control"
                          value={selectedService}
                          onChange={(e) => setSelectedService(e.target.value)}
                        >
                          <option value="">-- เลือกบริการ --</option>
                          {(serviceList || []).map((srv) => (
                            <option key={srv.Service_Id} value={srv.Service_Name}>
                              {srv.Service_Name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="text-center align-middle">
                        <div className="d-flex gap-1 justify-content-center flex-wrap">
                          {selectedService && (
                            <button
                              className="btn btn-primary btn-sm d-inline-flex align-items-center justify-content-center"
                              style={{ whiteSpace: "nowrap", gap: "6px", fontSize: "14px" }}
                              onClick={() => setShowStepModal(true)}
                              disabled={loadingSteps || serviceSteps.length === 0}
                            >
                              {loadingSteps ? (
                                <><Icon icon="mdi:loading" className="text-white animate-spin" />กำลังโหลด...</>
                              ) : (
                                <><Icon icon="mdi:plus-circle" className="text-white" />ขั้นตอน</>
                              )}
                            </button>
                          )}
                          <button
                            className="btn btn-sm d-inline-flex align-items-center justify-content-center"
                            style={{ whiteSpace: "nowrap", gap: "6px", fontSize: "14px", backgroundColor: "#059669", color: "white" }}
                            onClick={() => setSteps((prev) => [...prev, { id: uuid(), title: "", qty: "", unit: "", remark: "", checked: false, isCollect: false, itemGroup: null, isManual: true }])}
                          >
                            <Icon icon="mdi:plus" />
                            เพิ่มเอง
                          </button>
                        </div>
                      </td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                    </tr>

                    {steps && steps.length > 0 ? (
                      steps.map((st, idx) => (
                        <tr key={st.id}>
                          <td></td>
                          <td className="align-middle">
                            <div className="d-flex align-items-center gap-1">
                              <Icon icon="mdi:chevron-right" className="text-danger flex-shrink-0" style={{ marginRight: 4 }} />
                              <input
                                type="text"
                                className="form-control"
                                placeholder="ชื่อขั้นตอน"
                                value={st.title}
                                onChange={(e) => updateStep(idx, "title", e.target.value)}
                                onFocus={(e) => e.target.select()}
                                onKeyDown={(e) => handleStepKeyNav(e, idx, "title")}
                                data-stepidx={idx}
                                data-field="title"
                              />
                            </div>
                          </td>
                          <td className="align-middle" style={{ padding: "4px 6px" }}>
                            <SearchableItemSelect
                              value={st.itemId || null}
                              options={workItemList}
                              onChange={(item) => updateStepItem(idx, item)}
                              groupMap={groupMap}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control text-right tabular-nums"
                              value={focusedQtyIdx === idx && parseFloat(st.qty) > 0 ? st.qty : formatQtyDisplay(st.qty)}
                              onChange={(e) => updateStep(idx, "qty", sanitizeNumericInput(e.target.value))}
                              onFocus={(e) => {
                                setFocusedQtyIdx(idx);
                                const target = e.target;
                                setTimeout(() => target.select(), 0);
                              }}
                              onBlur={() => {
                                setFocusedQtyIdx(null);
                                const num = parseFloat(String(st.qty ?? "").replace(/,/g, ""));
                                if (!isNaN(num)) updateStep(idx, "qty", String(num));
                              }}
                              onKeyDown={(e) => handleStepKeyNav(e, idx, "qty")}
                              data-stepidx={idx}
                              data-field="qty"
                              placeholder="0"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              placeholder="หน่วย"
                              value={st.unit}
                              onChange={(e) => updateStep(idx, "unit", e.target.value)}
                              onFocus={(e) => e.target.select()}
                              onKeyDown={(e) => handleStepKeyNav(e, idx, "unit")}
                              data-stepidx={idx}
                              data-field="unit"
                              style={{ width: "100%", height: 34, padding: "0 8px", border: "1px solid #d1d5db", borderRadius: 4, backgroundColor: "#f3f4f6", color: "#111827", fontSize: 14, outline: "none", boxSizing: "border-box" }}
                            />
                          </td>
                          <td className="text-center align-middle">
                            {isTempEquipStep(st) ? (
                              <div className="d-flex align-items-center justify-content-center gap-2" style={{ whiteSpace: "nowrap", fontSize: 13 }}>
                                <label className="d-flex align-items-center gap-1 mb-0 cursor-pointer">
                                  <input
                                    type="radio"
                                    name={`collect-modal-${st.id}`}
                                    checked={!st.isCollect}
                                    onChange={() => updateStep(idx, "isCollect", false)}
                                    className="accent-[#E13F57]"
                                    style={{ width: 14, height: 14 }}
                                  />
                                  วางไว้
                                </label>
                                <label className="d-flex align-items-center gap-1 mb-0 cursor-pointer">
                                  <input
                                    type="radio"
                                    name={`collect-modal-${st.id}`}
                                    checked={!!st.isCollect}
                                    onChange={() => updateStep(idx, "isCollect", true)}
                                    className="accent-[#E13F57]"
                                    style={{ width: 14, height: 14 }}
                                  />
                                  เก็บคืน
                                </label>
                              </div>
                            ) : (
                              <span style={{ color: "#9ca3af" }}>-</span>
                            )}
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control"
                              value={st.remark}
                              onChange={(e) => updateStep(idx, "remark", e.target.value)}
                              onFocus={(e) => e.target.select()}
                              onKeyDown={(e) => handleStepKeyNav(e, idx, "remark")}
                              data-stepidx={idx}
                              data-field="remark"
                            />
                          </td>
                          <td className="text-center align-middle">
                            <button className="btn btn-danger btn-sm" onClick={() => removeStep(idx)}>
                              <Icon icon="mdi:delete" />
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="text-center text-muted py-3" style={{ fontSize: "14px" }}>
                          {selectedService ? "ยังไม่มีขั้นตอนการบริการ (กดปุ่ม 'ขั้นตอน' เพื่อเพิ่ม)" : "กรุณาเลือกบริการก่อน"}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn"
                style={{ backgroundColor: "#E13F57", color: "white", borderColor: "#E13F57", width: "120px" }}
                onClick={saveJob}
              >
                บันทึก
              </button>
              <button
                className="btn"
                style={{ backgroundColor: "#6b7280", color: "#fff", borderColor: "#6b7280", width: "120px" }}
                onClick={onClose}
              >
                ยกเลิก
              </button>
            </div>

          </div>
        </div>
      </div>

      <div className="modal-backdrop fade show"></div>

      <SelectStepModal
        isOpen={showStepModal}
        onClose={() => setShowStepModal(false)}
        serviceData={{
          Service_Id: selectedServiceId,
          Service_Name: selectedService,
          Steps: serviceSteps,
          selectedStepIds: new Set(steps.map((s) => s.id)),
        }}
        onSave={(selectedSteps) => {
          setSteps((prev) => {
            const existIds = new Set(prev.map((x) => x.id));
            const newItems = selectedSteps
              .filter((s) => !existIds.has(s.Step_Id))
              .map((s) => ({
                id: s.Step_Id,
                title: s.Step_Title || "",
                qty: s.Step_Qty != null ? String(s.Step_Qty) : "",
                unit: s.Step_Unit != null ? String(s.Step_Unit) : "",
                remark: "",
                checked: false,
                isCollect: false,
                itemGroup: s.Item_Group ?? null,
              }));
            return [...prev, ...newItems];
          });
        }}
      />
    </>
  );
}
